import {
  useListHealthConcerns,
  useListPatientCareProtocolOpportunities,
} from "@elation/api-client/src/stitch-web";

import { useCurrentPatient } from "@src/features/patients";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { MarkdownRenderer } from "@src/shared/components/markdown/MarkdownRenderer";

import { BriefingAccordion } from "./BriefingAccordion";
import { CodeChip } from "./CodeChip";
import styles from "./HealthConcerns.module.less";
import { Opportunity } from "./Opportunity";

interface HealthConcernsProps {
  newOpportunityIds: Set<number>;
}

export const HealthConcerns = ({ newOpportunityIds }: HealthConcernsProps) => {
  const currentPatient = useCurrentPatient();

  const { data: healthConcernsData } = useListHealthConcerns(
    currentPatient?.id ?? -1,
    {
      query: {
        enabled: !!currentPatient?.id,
      },
    },
  );
  const { data: opportunitiesData } = useListPatientCareProtocolOpportunities(
    currentPatient?.id ?? -1,
    {
      query: {
        enabled: !!currentPatient?.id,
      },
    },
  );

  const healthConcerns = healthConcernsData?.data ?? [];
  const conditionlessOpportunities = (opportunitiesData?.data ?? []).filter(
    (opportunity) => opportunity.healthConcernId === null,
  );

  if (healthConcerns.length === 0 && conditionlessOpportunities.length === 0) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        justifyContent="center"
        className="rounded-lg bg-muted/40 p-3 text-center"
      >
        <Text appearance="system">
          No care opportunities yet. Upload a gap list and we'll turn it into a
          ready-to-use agenda.
        </Text>
      </HorizontalFlexbox>
    );
  }

  return (
    <VerticalFlexbox as="ul" className={styles.root}>
      {healthConcerns.map((hc) => {
        const numberOfOpportunities = hc.opportunities?.length ?? 0;
        const codes = hc.codes ?? [];
        return (
          <li key={hc.id} data-id={hc.id}>
            <BriefingAccordion title={hc.name} subtitle={hc.diagnosticStatus}>
              <VerticalFlexbox className={styles.content}>
                <MarkdownRenderer markdown={hc.description} />
                {codes.length > 0 && (
                  <div className={styles.codeChipRow}>
                    {codes.map((c, i) => (
                      <CodeChip
                        key={`${c.codeType}-${c.code}-${i}`}
                        codeType={c.codeType}
                        code={c.code}
                        description={c.description}
                        codeRole={c.role}
                      />
                    ))}
                  </div>
                )}
                {numberOfOpportunities > 0 && (
                  <VerticalFlexbox as="ul" className={styles.opportunities}>
                    {hc.opportunities?.map((opportunity) => (
                      <li key={opportunity.id}>
                        <Opportunity
                          opportunity={opportunity}
                          isNew={newOpportunityIds.has(opportunity.id)}
                        />
                      </li>
                    ))}
                  </VerticalFlexbox>
                )}
              </VerticalFlexbox>
            </BriefingAccordion>
          </li>
        );
      })}
      {conditionlessOpportunities.length > 0 && (
        <li data-id="conditionless-opportunities">
          <BriefingAccordion
            title="Other care opportunities"
            subtitle="Patient-level"
          >
            <VerticalFlexbox as="ul" className={styles.opportunities}>
              {conditionlessOpportunities.map((opportunity) => (
                <li key={opportunity.id}>
                  <Opportunity
                    opportunity={opportunity}
                    isNew={newOpportunityIds.has(opportunity.id)}
                  />
                </li>
              ))}
            </VerticalFlexbox>
          </BriefingAccordion>
        </li>
      )}
    </VerticalFlexbox>
  );
};
